<template>
  <div>
    <WdsText secondary class="tw-mb-4">
      {{
        $t(
          'The SNA quality mark certifies that your agency meets NEN 4400-1 standards. This affects your G-Account split percentage.',
        )
      }}
    </WdsText>

    <WdsRadioGroup
      v-model:checked="snaStatus"
      :label="$t('Do you hold a valid SNA quality mark (NEN 4400-1)?')"
      name="sna_status"
    >
      <WdsRadio :value="NlSnaStatus.CERTIFIED">
        {{ $t('Yes, we are SNA certified') }}
        <WdsText secondary size="small" class="tw-mt-1">
          {{ $t('G-Account split: 25% of invoice total') }}
        </WdsText>
      </WdsRadio>
      <WdsRadio :value="NlSnaStatus.NOT_CERTIFIED">
        {{ $t('No, we are not SNA certified.') }}
        <WdsText secondary size="small" class="tw-mt-1">
          {{ $t('G-Account split: 55% of invoice total.') }}
        </WdsText>
      </WdsRadio>
      <WdsRadio :value="NlSnaStatus.NOT_APPLICABLE">
        {{ $t('SNA certification does not apply to us') }}
        <WdsText secondary size="small" class="tw-mt-1">
          {{ $t('G-Account is not required.') }}
        </WdsText>
      </WdsRadio>
    </WdsRadioGroup>

    <WdsAlert
      v-if="errorMessage && showError"
      class="tw-mt-4"
      closable
      error
      @close="showError = false"
    >
      {{ $t('An error occurred') }}
      <template #description>
        {{ errorMessage }}
      </template>
    </WdsAlert>

    <WdsAlert v-else-if="showSuccess" class="tw-mt-4" closable success @close="showSuccess = false">
      {{ $t('Your SNA certification status has been saved') }}
    </WdsAlert>

    <div class="tw-mt-4">
      <WdsButton :disabled="loading || !snaStatus" primary @click="onSave">
        <div class="tw-flex tw-items-center">
          <span>{{ $t('Save') }}</span>
          <WdsSpinner v-show="loading" class="tw-ml-2" indeterminate>
            <WdsIcon name="bam" :size="16" />
          </WdsSpinner>
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsIcon,
  WdsRadio,
  WdsRadioGroup,
  WdsSpinner,
  WdsText,
} from '@worksome/wds-components'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { NlSnaStatus } from '@/components/compliances/types/ComplianceName.enum'
import { FormValidation } from '@/utils/formValidation'

export default {
  name: 'NlSnaCertificationSettingsForm',
  components: {
    WdsAlert,
    WdsButton,
    WdsIcon,
    WdsRadio,
    WdsRadioGroup,
    WdsSpinner,
    WdsText,
  },
  props: {
    recruiter: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
  },
  emits: ['saved'],
  setup(props) {
    const errors = ref([])
    const internalComplianceData = reactive(props.complianceData)
    const loading = ref(false)
    const showError = ref(false)
    const showSuccess = ref(false)

    return {
      errors,
      internalComplianceData,
      loading,
      showError,
      showSuccess,
      NlSnaStatus,
    }
  },
  computed: {
    snaStatus: {
      get() {
        const data = this.internalComplianceData
        if (data.snaStatus) {
          return data.snaStatus
        }
        if (data.isCertified === true) return NlSnaStatus.CERTIFIED
        if (data.isCertified === false) return NlSnaStatus.NOT_CERTIFIED
        return null
      },
      set(value) {
        this.internalComplianceData.snaStatus = value
      },
    },
    errorMessage() {
      return FormValidation.containsErrorMessage(this.errors)
    },
  },
  methods: {
    async onSave() {
      this.loading = true

      const data = JSON.stringify({
        sna_status: this.snaStatus,
      })

      try {
        await this.$apollo.mutate({
          mutation: UpdateComplianceMutation,
          variables: {
            input: {
              name: 'NL_SNA_CERTIFICATION',
              data,
            },
          },
        })

        this.showSuccess = true
        this.showError = false

        this.$emit('saved')
      } catch ({ graphQLErrors }) {
        this.errors = graphQLErrors
        this.showError = true
        this.showSuccess = false
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
